import { LocalDate } from "@js-joda/core";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { DatePicker } from "components/designV2/datePicker/datePicker";
import { Modal } from "components/designV2/modal/modal";
import { PrimaryButton } from "components/designV2/buttons/primaryButton";
import { SecondaryButton } from "components/designV2/buttons/secondaryButton";
import { Api } from "services/api/api.service";
import type { CompanyGigShift } from "model/Gig";
import type { CopySchedulePayload } from "services/api/company/gig.service";
import type { Worker as ModalWorker } from "web-apps/company/components/organizationalStructure/chooseWorkersModal.types";
import {
  RepeatScheduleBody,
  RepeatScheduleDatePickerWrap,
  RepeatScheduleField,
  RepeatScheduleFieldList,
  RepeatScheduleFooter,
  RepeatScheduleIntro,
} from "./repeatScheduleModal.styled";

interface RepeatScheduleModalProps {
  isOpen: boolean;
  companyId: number | null;
  sourceWorker: ModalWorker | null;
  initialCopyFromDates: LocalDate[];
  initialCreateInDates: LocalDate[];
  onCopyScheduleSuccess: (
    shifts: CompanyGigShift[],
  ) => CopyScheduleSuccessResult;
  onClose: () => void;
}

interface CopyScheduleSuccessResult {
  addedShiftCount: number;
}

type CopyScheduleResult =
  | { type: "idle" }
  | { type: "success"; shiftCount: number }
  | { type: "error" };

const getDatePickerKey = (dates: LocalDate[]): string =>
  dates.map((date) => date.toString()).join("|");

export const RepeatScheduleModal: React.FC<RepeatScheduleModalProps> = (
  props,
) => {
  const { t } = useTranslation();
  const [copyScheduleResult, setCopyScheduleResult] =
    useState<CopyScheduleResult>({
      type: "idle",
    });
  const [copyFromDates, setCopyFromDates] = useState<LocalDate[]>(
    props.initialCopyFromDates,
  );
  const [createInDates, setCreateInDates] = useState<LocalDate[]>(
    props.initialCreateInDates,
  );

  useEffect(() => {
    if (!props.isOpen) {
      setCopyScheduleResult({ type: "idle" });
      return;
    }
    setCopyFromDates(props.initialCopyFromDates);
    setCreateInDates(props.initialCreateInDates);
  }, [props.initialCopyFromDates, props.initialCreateInDates, props.isOpen]);

  const copyFromDatePickerKey = useMemo(
    () => `copy-${getDatePickerKey(props.initialCopyFromDates)}`,
    [props.initialCopyFromDates],
  );
  const createInDatePickerKey = useMemo(
    () => `create-${getDatePickerKey(props.initialCreateInDates)}`,
    [props.initialCreateInDates],
  );
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const canSubmit =
    props.companyId != null &&
    props.sourceWorker != null &&
    copyFromDates.length > 0 &&
    createInDates.length > 0;
  const sourceWorkerName = props.sourceWorker
    ? `${props.sourceWorker.firstName} ${props.sourceWorker.lastName}`.trim()
    : "";
  const isResultModalOpen = copyScheduleResult.type !== "idle";

  const handleClose = (): void => {
    if (isSubmitting) return;
    setCopyScheduleResult({ type: "idle" });
    props.onClose();
  };

  const getResultModalLabel = (): string => {
    if (copyScheduleResult.type === "error") {
      return t("CreateShift.RepeatScheduleErrorTitle");
    }

    return t("CreateShift.RepeatScheduleResultTitle");
  };

  const getResultModalMessage = (): string => {
    if (copyScheduleResult.type === "error") {
      return t("CreateShift.RepeatScheduleErrorMessage");
    }

    if (copyScheduleResult.type === "success") {
      if (copyScheduleResult.shiftCount === 0) {
        return t("CreateShift.RepeatScheduleNoShiftsAdded");
      }

      return t("CreateShift.RepeatScheduleShiftsAdded", {
        count: copyScheduleResult.shiftCount,
      });
    }

    return "";
  };

  const handleCreateDraft = async (): Promise<void> => {
    if (!canSubmit || props.companyId == null) return;
    const sourceWorker = props.sourceWorker;
    if (sourceWorker == null) return;

    const payload: CopySchedulePayload = {
      workerId: sourceWorker.workerId ?? sourceWorker.id,
      copyFromDates,
      createInDates,
    };

    setIsSubmitting(true);
    try {
      const response = await Api().company.gig.copySchedule(
        props.companyId,
        payload,
      );
      const result = props.onCopyScheduleSuccess(response.data);
      setCopyScheduleResult({
        type: "success",
        shiftCount: result.addedShiftCount,
      });
    } catch {
      setCopyScheduleResult({ type: "error" });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Modal
        label={t("CreateShift.RepeatSchedule")}
        size="small"
        isOpen={props.isOpen && !isResultModalOpen}
        onClose={handleClose}
        footer={
          <RepeatScheduleFooter>
            <SecondaryButton
              label={t("DesignComponents.Cancel")}
              size="small"
              onClick={handleClose}
              disabled={isSubmitting}
            />
            <PrimaryButton
              label={t("CreateShift.CreateDraft")}
              size="small"
              onClick={() => {
                void handleCreateDraft();
              }}
              disabled={!canSubmit}
              isLoading={isSubmitting}
            />
          </RepeatScheduleFooter>
        }
      >
        <RepeatScheduleBody>
          <RepeatScheduleIntro>
            {t("CreateShift.RepeatScheduleDescription", {
              workerName: sourceWorkerName,
            })}
          </RepeatScheduleIntro>

          <RepeatScheduleFieldList>
            <RepeatScheduleField>
              <RepeatScheduleDatePickerWrap>
                <DatePicker
                  key={copyFromDatePickerKey}
                  label={t("CreateShift.CopyFromPeriod")}
                  value={copyFromDates[0] ?? null}
                  rangeValue={copyFromDates}
                  onRangeChange={setCopyFromDates}
                  allowRangePicker
                  rangeOnly
                  allowClear
                />
              </RepeatScheduleDatePickerWrap>
            </RepeatScheduleField>

            <RepeatScheduleField>
              <RepeatScheduleDatePickerWrap>
                <DatePicker
                  key={createInDatePickerKey}
                  label={t("CreateShift.CreateScheduleInPeriod")}
                  value={createInDates[0] ?? null}
                  rangeValue={createInDates}
                  onRangeChange={setCreateInDates}
                  allowRangePicker
                  rangeOnly
                  allowClear
                />
              </RepeatScheduleDatePickerWrap>
            </RepeatScheduleField>
          </RepeatScheduleFieldList>
        </RepeatScheduleBody>
      </Modal>

      <Modal
        label={getResultModalLabel()}
        size="compact"
        isOpen={props.isOpen && isResultModalOpen}
        onClose={handleClose}
        showCloseButton={false}
        allowContentOverflow
        centerText
        footer={<PrimaryButton label={t("General.Ok")} onClick={handleClose} />}
      >
        <RepeatScheduleIntro>{getResultModalMessage()}</RepeatScheduleIntro>
      </Modal>
    </>
  );
};
